<template>
  <div class="tasks-optimized">
    <!-- Loading discreto nell'angolo -->
    <v-fade-transition>
      <div v-if="loading" class="discrete-loader">
        <v-progress-circular indeterminate color="success" size="16" width="2"></v-progress-circular>
        <span class="ml-2 text-caption">{{ loadingTime }}</span>
      </div>
    </v-fade-transition>

    <!-- Contenuto principale -->
    <div>
      <!-- Header semplice e pulito -->
      <div class="mb-4">

        <!-- Cards widget -->
        <v-row>
          <!-- Selezione Progetto -->
          <v-col cols="12" md="4">
            <v-card flat outlined>
              <v-card-title class="pb-2">
                <v-icon color="primary" class="mr-2">mdi-folder-search</v-icon>
                <span>Progetto</span>
              </v-card-title>
              <v-card-text class="pt-0">
                <if65-autocomplete
                  id="main-project-selector"
                  v-model="projectId"
                  :api="() => $projectService.list_names({choices: ''})"
                  label="Cerca progetto..."
                  placeholder="Seleziona progetto..."
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  style="width: 100%;" />
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Statistiche Ore -->
          <v-col cols="12" md="4">
            <v-card flat outlined>
              <v-card-title class="pb-2">
                <v-icon color="success" class="mr-2">mdi-clock-outline</v-icon>
                <span>Statistiche Ore</span>
              </v-card-title>
              <v-card-text class="pt-0">
                <div class="d-flex justify-space-between mb-2">
                  <span>Ore oggi:</span>
                  <span class="font-weight-bold success--text">{{ dailyHours }}h</span>
                </div>
                <div class="d-flex justify-space-between">
                  <span>Ore totali:</span>
                  <span class="font-weight-bold primary--text">{{ totalHours }}h</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Team Operatori -->
          <v-col cols="12" md="4" v-if="$store.state.user && ['ADMIN', 'HEAD', 'PURCHASE_OPERATOR'].includes($store.state.user.role)">
            <v-card flat outlined>
              <v-card-title class="pb-2">
                <v-icon color="warning" class="mr-2">mdi-account-clock</v-icon>
                <span>Team Oggi</span>
                <v-spacer></v-spacer>
                <small class="text-caption">{{ todayDate }}</small>
              </v-card-title>
              <v-card-text class="pt-0" style="max-height: 120px; overflow-y: auto;">
                <div v-if="dailyOperatorStats.length === 0" class="text-center text-caption grey--text py-2">
                  Nessun task registrato oggi
                </div>
                <div
                  v-for="operator in dailyOperatorStats"
                  :key="operator.id"
                  class="d-flex align-center justify-space-between py-1"
                >
                  <div class="d-flex align-center">
                    <v-avatar size="24" color="primary" class="mr-2">
                      <span class="white--text text-caption">{{ operator.initials }}</span>
                    </v-avatar>
                    <span class="text-body-2">{{ operator.name }}</span>
                  </div>
                  <span class="text-body-2 font-weight-bold">{{ operator.hours }}h</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Spazio vuoto se non ci sono operatori -->
          <v-col cols="12" md="4" v-else>
            <v-card flat outlined class="d-flex align-center justify-center" style="min-height: 140px;">
              <div class="text-center">
                <v-icon size="36" color="grey lighten-1">mdi-chart-line</v-icon>
                <div class="text-subtitle-2 grey--text mt-2">Statistiche</div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Filtri combinati in un'unica card -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card flat class="pa-3">
            <!-- Prima riga: Search, Quick filters e azioni -->
            <div class="d-flex align-center pb-3">
              <!-- Campo ricerca globale -->
              <v-text-field
                id="global-search-input"
                v-model="searchQuery"
                @input="searchDebounced"
                placeholder="Cerca in tutti i campi..."
                dense
                outlined
                hide-details
                clearable
                style="max-width: 250px; min-width: 200px; height: 32px;"
                class="mr-3 small-search-field"
              >
                <template v-slot:append>
                  <v-icon small :class="{ 'mdi-spin': isSearching }" color="primary">
                    {{ isSearching ? 'mdi-loading' : 'mdi-magnify' }}
                  </v-icon>
                </template>
              </v-text-field>

              <!-- Chip filtri rapidi -->
              <v-chip-group
                v-model="quickFilter"
                active-class="primary--text"
                mandatory
                class="mr-3"
              >
                <v-chip small filter outlined>Tutti</v-chip>
                <v-chip small filter outlined>Oggi</v-chip>
                <v-chip small filter outlined>Questa settimana</v-chip>
                <v-chip small filter outlined>Questo mese</v-chip>
              </v-chip-group>

              <!-- Spacer -->
              <v-spacer></v-spacer>

              <!-- Bottoni azione e conteggio -->
              <div class="d-flex align-center">
                <v-btn @click="clearFilters" outlined small class="mr-2">
                  <v-icon left small>mdi-filter-off</v-icon>
                  Reset
                </v-btn>
                <v-tooltip bottom v-if="!$store.getters.canExportData">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      @click="exportTasks"
                      color="grey"
                      disabled
                      small
                      elevation="0"
                      class="mr-2 font-weight-bold"
                      v-bind="attrs"
                      v-on="on">
                      <v-icon left small>mdi-file-excel</v-icon>
                      Esporta Excel
                    </v-btn>
                  </template>
                  <span>Non hai i permessi per esportare i dati</span>
                </v-tooltip>

                <v-btn
                  v-if="$store.getters.canExportData"
                  @click="exportTasks"
                  color="success"
                  small
                  elevation="2"
                  class="mr-2 font-weight-bold">
                  <v-icon left small>mdi-file-excel</v-icon>
                  Esporta Excel
                </v-btn>
                <column-selector
                  v-model="visibleColumns"
                  :columns="availableColumns"
                  class="mr-3"
                ></column-selector>
              </div>
            </div>

            <!-- Divider -->
            <v-divider></v-divider>

            <!-- Seconda riga: Filtri colonne -->
            <div class="d-flex align-center pt-3 flex-wrap justify-space-between">
              <!-- Sezione filtri (sinistra) -->
              <div class="d-flex align-center flex-wrap">
                <!-- Filtro Customer/Insegne -->
              <v-menu
                v-if="isColumnVisible('customer')"
                v-model="customerMenu"
                :close-on-content-click="false"
                offset-y
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    outlined
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-2 mb-2"
                  >
                    <v-icon left small>mdi-store</v-icon>
                    Insegne
                    <v-badge
                      v-if="filters.customer"
                      dot
                      color="success"
                      overlap
                      x-small
                    ></v-badge>
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="pb-2">
                    Filtra per Insegna
                    <v-spacer></v-spacer>
                    <v-btn text small @click="filters.customer = null">Reset</v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-2">
                    <if65-autocomplete
                      id="filter-customer-selector"
                      v-model="filters.customer"
                      :api="safeCustomerService"
                      label=""
                      placeholder="Cerca insegna..."
                      dense
                      outlined
                      clearable
                      hide-details
                    />
                  </v-card-text>
                </v-card>
              </v-menu>

              <!-- Filtro Operatori -->
              <v-menu
                v-if="isColumnVisible('operator')"
                v-model="operatorsMenu"
                :close-on-content-click="false"
                offset-y
                max-width="300"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    outlined
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-2 mb-2"
                  >
                    <v-icon left small>mdi-account</v-icon>
                    Operators
                    <v-badge
                      v-if="selectedOperators.length > 0"
                      :content="selectedOperators.length"
                      color="error"
                      overlap
                      x-small
                    ></v-badge>
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="pb-2">
                    Filtra per Operatori
                    <v-spacer></v-spacer>
                    <v-btn text small @click="selectedOperators = []">Reset</v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-2" style="max-height: 250px; overflow-y: auto;">
                    <v-checkbox
                      v-for="operator in allOperators"
                      :key="operator.id"
                      :id="'operator-checkbox-' + operator.id"
                      :value="operator.id"
                      v-model="selectedOperators"
                      dense
                      hide-details
                      class="ma-0 pa-1"
                    >
                      <template v-slot:label>
                        <div class="d-flex align-center">
                          <v-avatar size="20" color="primary" class="mr-2">
                            <span class="white--text text-caption">{{ operator.initials }}</span>
                          </v-avatar>
                          <span class="text-body-2">{{ operator.full_name }}</span>
                        </div>
                      </template>
                    </v-checkbox>
                  </v-card-text>
                </v-card>
              </v-menu>

              <!-- Filtro Project Code -->
              <v-menu
                v-if="isColumnVisible('project_code')"
                v-model="projectCodeMenu"
                :close-on-content-click="false"
                offset-y
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    outlined
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-2 mb-2"
                  >
                    <v-icon left small>mdi-tag</v-icon>
                    Codice Progetto
                    <v-badge
                      v-if="projectCode"
                      dot
                      color="success"
                      overlap
                      x-small
                    ></v-badge>
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="pb-2">
                    Filtra per Codice Progetto
                    <v-spacer></v-spacer>
                    <v-btn text small @click="projectCode = null">Reset</v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-2">
                    <if65-autocomplete
                      id="filter-project-code-selector"
                      v-model="projectCode"
                      :api="projectCodeApi"
                      label=""
                      placeholder="Cerca codice progetto..."
                      dense
                      outlined
                      clearable
                      hide-details
                      return-object
                      item-text="text"
                      item-value="value"
                    />
                  </v-card-text>
                </v-card>
              </v-menu>

              <!-- Filtro Activities -->
              <v-menu
                v-if="isColumnVisible('activity_name')"
                v-model="activitiesMenu"
                :close-on-content-click="false"
                offset-y
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    outlined
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-2 mb-2"
                  >
                    <v-icon left small>mdi-format-list-bulleted</v-icon>
                    Attività
                    <v-badge
                      v-if="filters.activities"
                      dot
                      color="success"
                      overlap
                      x-small
                    ></v-badge>
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="pb-2">
                    Filtra per Attività
                    <v-spacer></v-spacer>
                    <v-btn text small @click="filters.activities = null">Reset</v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-2">
                    <if65-autocomplete
                      id="filter-activities-selector"
                      v-model="filters.activities"
                      :api="safeActivityService"
                      label=""
                      placeholder="Cerca attività..."
                      dense
                      outlined
                      clearable
                      hide-details
                    />
                  </v-card-text>
                </v-card>
              </v-menu>

              <!-- Filtro Products -->
              <v-menu
                v-if="isColumnVisible('product_name')"
                v-model="productsMenu"
                :close-on-content-click="false"
                offset-y
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    outlined
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-2 mb-2"
                  >
                    <v-icon left small>mdi-package</v-icon>
                    Prodotti
                    <v-badge
                      v-if="filters.products"
                      dot
                      color="success"
                      overlap
                      x-small
                    ></v-badge>
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="pb-2">
                    Filtra per Prodotti
                    <v-spacer></v-spacer>
                    <v-btn text small @click="filters.products = null">Reset</v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-2">
                    <if65-autocomplete
                      id="filter-products-selector"
                      v-model="filters.products"
                      :api="safeProductService"
                      label=""
                      placeholder="Cerca prodotti..."
                      dense
                      outlined
                      clearable
                      hide-details
                    />
                  </v-card-text>
                </v-card>
              </v-menu>

              <!-- Filtro Date (apertura diretta) -->
              <div v-if="isColumnVisible('task_date')" class="mr-2 mb-2">
                <if65-date-picker
                  id="filter-date-range-picker"
                  v-model="createdAtRangeToSearch"
                  label="Data Creazione"
                  dense
                  small
                  outlined
                  color="primary"
                  :range="true"
                  dateFormat="dd/MM/yyyy"
                  icon="mdi-calendar"
                  style="min-width: 160px; max-width: 160px;"
                  :hide-details="true"
                />
              </div>

              <!-- Filtro Order Number -->
              <v-menu
                v-if="isColumnVisible('order_number')"
                v-model="orderMenu"
                :close-on-content-click="false"
                offset-y
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    outlined
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-2 mb-2"
                  >
                    <v-icon left small>mdi-receipt</v-icon>
                    N. Ordine
                    <v-badge
                      v-if="orderNumberToSearch"
                      dot
                      color="success"
                      overlap
                      x-small
                    ></v-badge>
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="pb-2">
                    Cerca Numero Ordine
                    <v-spacer></v-spacer>
                    <v-btn text small @click="orderNumberToSearch = ''">Reset</v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-2">
                    <v-text-field
                      id="filter-order-number-input"
                      v-model="orderNumberToSearch"
                      placeholder="Cerca numero ordine..."
                      dense
                      outlined
                      clearable
                      hide-details
                    />
                  </v-card-text>
                </v-card>
              </v-menu>
              </div>

              <!-- Sezione widget statistiche (destra) -->
              <div class="d-flex align-center flex-wrap">
                <!-- Widget Task -->
                <v-card flat outlined class="mr-3 pa-2" style="min-width: 120px;">
                  <div class="text-center">
                    <div class="text-h6 primary--text">{{ tasks.length }}/{{ totalItems }}</div>
                    <div class="text-caption">Task</div>
                  </div>
                </v-card>

                <!-- Widget Ore -->
                <v-card flat outlined class="mr-3 pa-2" style="min-width: 120px;">
                  <div class="text-center">
                    <div class="text-h6 success--text">{{ (taskStats?.total_hours || 0) }}/{{ totalFilteredHours || 0 }}</div>
                    <div class="text-caption">Ore</div>
                  </div>
                </v-card>

                <!-- Widget Progetto (se selezionato) -->
                <v-card v-if="project" flat outlined class="pa-2" style="min-width: 140px;">
                  <div class="text-center">
                    <div class="text-body-2 font-weight-medium primary--text text-truncate">{{ project }}</div>
                    <div class="text-caption">Progetto</div>
                  </div>
                </v-card>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Indicatore risultati ricerca -->
      <v-alert
        v-if="searchQuery"
        type="info"
        dense
        outlined
        class="mb-3"
      >
        <div class="d-flex align-center">
          <v-icon left small>mdi-magnify</v-icon>
          <span v-if="tasks.length > 0">
            <v-chip small color="success" class="mr-2">{{ totalItems }} risultati</v-chip>
            trovati per "<em>{{ searchQuery }}</em>"
            <span class="text--secondary ml-2">(server-side)</span>
          </span>
          <span v-else>
            <strong>Nessun risultato</strong> per "<em>{{ searchQuery }}</em>" in questa pagina
          </span>
        </div>
      </v-alert>

      <!-- Tabella ottimizzata -->
      <v-card flat>
        <!-- Messaggio nessun risultato -->
        <div v-if="searchQuery && tasks.length === 0" class="text-center pa-8">
          <v-icon size="64" color="grey lighten-2">mdi-magnify</v-icon>
          <div class="text-h6 mt-2 grey--text">Nessun risultato trovato</div>
          <div class="text-caption grey--text">
            Prova con un altro termine di ricerca o naviga tra le pagine
          </div>
        </div>

        <div class="table-container" v-else>
          <table class="tasks-table">
            <thead>
              <!-- Riga titoli colonne -->
              <tr>
                <th v-if="isColumnVisible('customer')" width="60">Ins</th>
                <th v-if="isColumnVisible('operator')" @click="sortBy('operator_name')" class="sortable text-center" width="60">
                  Op
                  <v-icon small>{{ getSortIcon('operator_name') }}</v-icon>
                </th>
                <th v-if="isColumnVisible('project_code')" @click="sortBy('project_code')" class="sortable" width="80">
                  Cod
                  <v-icon small>{{ getSortIcon('project_code') }}</v-icon>
                </th>
                <th v-if="isColumnVisible('project_name')" @click="sortBy('project_name')" class="sortable" width="140">
                  Prog
                  <v-icon small>{{ getSortIcon('project_name') }}</v-icon>
                </th>
                <th v-if="isColumnVisible('activity_name')" width="100">Att</th>
                <th v-if="isColumnVisible('product_name')" width="140">Prod</th>
                <th v-if="isColumnVisible('sub_activity_name')" width="100">Sub</th>
                <th v-if="isColumnVisible('task_date')" @click="sortBy('task_date')" class="sortable" width="80">
                  Data
                  <v-icon small>{{ getSortIcon('task_date') }}</v-icon>
                </th>
                <th v-if="isColumnVisible('order_number')" width="80">Ord</th>
                <th v-if="isColumnVisible('hours')" @click="sortBy('hours')" class="sortable text-right" width="60">
                  Ore
                  <v-icon small>{{ getSortIcon('hours') }}</v-icon>
                </th>
                <th v-if="isColumnVisible('actions')" width="60">Az</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="task in paginatedTasks"
                :key="task.id"
                class="task-row"
                :class="{ 'task-row--today': isToday(task.task_date) }"
              >
                <!-- Insegna -->
                <td v-if="isColumnVisible('customer')">{{ truncate(task.customer, 12) }}</td>

                <!-- Operatore -->
                <td v-if="isColumnVisible('operator')" class="text-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-avatar
                        size="24"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <span class="white--text text-caption">
                          {{ getInitials(task.operator_full_name || task.operator_name) }}
                        </span>
                      </v-avatar>
                    </template>
                    <span>{{ task.operator_full_name || task.operator_name || 'Operatore non specificato' }}</span>
                  </v-tooltip>
                </td>

                <!-- Codice progetto -->
                <td v-if="isColumnVisible('project_code')">{{ task.project_code }}</td>

                <!-- Progetto -->
                <td v-if="isColumnVisible('project_name')">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ truncate(task.project_name, 15) }}</span>
                    </template>
                    <span>{{ task.project_name }}</span>
                  </v-tooltip>
                </td>

                <!-- Attività -->
                <td v-if="isColumnVisible('activity_name')">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ truncate(task.activity_name, 12) }}</span>
                    </template>
                    <span>{{ task.activity_name }}</span>
                  </v-tooltip>
                </td>

                <!-- Prodotto -->
                <td v-if="isColumnVisible('product_name')">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ truncate(task.product_name, 15) }}</span>
                    </template>
                    <span>{{ task.product_name }}</span>
                  </v-tooltip>
                </td>

                <!-- Sotto-attività -->
                <td v-if="isColumnVisible('sub_activity_name')">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ truncate(task.sub_activity_name, 12) }}</span>
                    </template>
                    <span>{{ task.sub_activity_name }}</span>
                  </v-tooltip>
                </td>

                <!-- Data -->
                <td v-if="isColumnVisible('task_date')">
                  <div class="date-cell">
                    <div class="date-main">{{ formatDate(task.task_date, 'dd/MM') }}</div>
                    <div class="date-year">{{ formatDate(task.task_date, 'yyyy') }}</div>
                  </div>
                </td>

                <!-- Numero ordine -->
                <td v-if="isColumnVisible('order_number')">{{ task.order_number ? truncate(task.order_number, 8) : '-' }}</td>

                <!-- Ore -->
                <td v-if="isColumnVisible('hours')" class="text-right">
                  <v-chip
                    :color="getHoursColor(task.hours)"
                    small
                    text-color="white"
                    class="font-weight-bold"
                  >
                    {{ task.hours }}h
                  </v-chip>
                </td>

                <!-- Azioni -->
                <td v-if="isColumnVisible('actions')">
                  <div class="action-buttons">
                    <v-btn
                      icon
                      x-small
                      @click="editTask(task)"
                      color="primary"
                      :disabled="!canEditTask(task)"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      @click="deleteTask(task)"
                      color="error"
                      :disabled="!canDelete(task)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginazione compatta -->
        <div class="d-flex justify-space-between align-center pa-3">
          <div class="text-caption text--secondary">
            Mostrando {{ startIndex }}-{{ endIndex }} di {{ totalItems }} task
          </div>

          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            circle
            color="primary"
            small
            class="ma-0"
          ></v-pagination>
        </div>
      </v-card>

    </div>

    <!-- Dialoghi minimali -->
    <task-dialog-minimal
      v-model="showTaskDialog"
      :task-id="editingTaskId"
      @saved="onTaskSaved"
    />

    <confirm-dialog
      v-model="showDeleteDialog"
      title="Elimina Task"
      text="Sei sicuro di voler eliminare questo task?"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import { format, parseISO, isToday as checkIsToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { debounce } from 'lodash';
import If65Autocomplete from '../../components/widgets/If65Autocomplete.vue';
// If65MultiSelect rimosso - utilizziamo solo If65Autocomplete per ricerca server-side
import If65DatePicker from '../../components/widgets/If65DatePicker.vue';
import ColumnSelector from '../../components/ColumnSelector.vue';
import axios from 'axios';
import authHeader from '/src/services/auth.header';

export default {
  name: 'TasksProject2',
  components: {
    // Componenti essenziali
    If65Autocomplete,
    // If65MultiSelect rimosso
    If65DatePicker,
    ColumnSelector,
    // Lazy load dei componenti pesanti
    TaskDialogMinimal: () => import('@/components/dialogs/TaskDialogMinimal.vue'),
    ConfirmDialog: () => import('@/components/dialogs/ConfirmDialog.vue')
  },
  props: {
    selectedProjectId: {
      default: null
    }
  },
  data() {
    return {
      loading: true,

      // Timer per loading performance
      loadingStartTime: null,
      loadingTime: "0.00s",
      loadingTimer: null,

      // Dati principali (cache locale)
      tasks: [],
      taskStats: null,
      totalFilteredHours: 0,
      project: null,

      // Selezione progetto
      projectId: this.selectedProjectId,

      // Statistiche operatori giornaliere
      dailyOperatorStats: [],

      // Lista completa operatori per filtro
      allOperators: [],

      // Filtri e ricerca
      searchQuery: '',
      selectedDate: null,
      // Per i filtri rapidi che richiedono range
      dateRangeStart: null,
      dateRangeEnd: null,
      dateRange: '',
      createdAtRangeToSearch: [], // Range di date avanzato
      dateMenu: false,
      customerMenu: false,
      projectCodeMenu: false,
      operatorsMenu: false,
      activitiesMenu: false,
      productsMenu: false,
      orderMenu: false,
      quickFilter: 0,
      selectedOperators: [], // Filtro operatori

      // Nuovi filtri dal TasksProject.vue originale
      filters: {
        customer: null,
        project_name: [],
        activities: null, // Cambiato da array a valore singolo per autocomplete
        products: null,   // Cambiato da array a valore singolo per autocomplete
        sub_activities: []
      },
      projectCode: null, // Codice progetto per autocomplete
      orderNumberToSearch: '', // Numero ordine

      // Ordinamento
      sortField: 'task_date',
      sortDirection: 'desc',

      // Paginazione SERVER-SIDE
      currentPage: 1,
      itemsPerPage: 50,
      totalItems: 0,
      totalPages: 0,

      // Dialoghi
      showTaskDialog: false,
      showDeleteDialog: false,
      editingTaskId: null,
      deletingTask: null,

      // Performance
      searchDebounced: null,
      searchTimeout: null,
      searchAbortController: null,
      isSearching: false,

      // Gestione colonne visibili
      availableColumns: [
        { key: 'customer', label: 'Insegna', icon: 'mdi-store' },
        { key: 'operator', label: 'Operatore', icon: 'mdi-account' },
        { key: 'project_code', label: 'Codice', icon: 'mdi-tag' },
        { key: 'project_name', label: 'Progetto', icon: 'mdi-folder' },
        { key: 'activity_name', label: 'Attività', icon: 'mdi-format-list-bulleted' },
        { key: 'product_name', label: 'Prodotto', icon: 'mdi-package' },
        { key: 'sub_activity_name', label: 'Sub-attività', icon: 'mdi-format-list-bulleted-triangle' },
        { key: 'task_date', label: 'Data', icon: 'mdi-calendar' },
        { key: 'order_number', label: 'Ordine', icon: 'mdi-receipt' },
        { key: 'hours', label: 'Ore', icon: 'mdi-clock' },
        { key: 'actions', label: 'Azioni', icon: 'mdi-cog' }
      ],
      visibleColumns: [
        'customer', 'operator', 'project_code', 'project_name',
        'activity_name', 'product_name', 'sub_activity_name',
        'task_date', 'hours', 'actions'
      ]
    }
  },
  computed: {
    // Data di oggi formattata
    todayDate() {
      return format(new Date(), 'dd/MM/yyyy');
    },

    // Ore giornaliere (calcolate dai task di oggi)
    dailyHours() {
      const today = new Date().toISOString().substr(0, 10);
      const todayTasks = this.tasks.filter(task =>
        task.task_date === today
      );
      const totalMinutes = todayTasks.reduce((sum, task) => sum + (task.minutes || 0), 0);
      return Math.round((totalMinutes / 60) * 100) / 100;
    },

    // Ore totali
    totalHours() {
      return this.taskStats?.total_hours || 0;
    },

    // Controlla se ci sono filtri attivi
    hasActiveFilters() {
      return this.selectedOperators.length > 0 ||
             this.filters.customer ||
             this.searchQuery?.trim() ||
             this.selectedDate ||
             this.projectId ||
             this.orderNumberToSearch?.trim() ||
             (this.createdAtRangeToSearch && this.createdAtRangeToSearch.length > 0) ||
             (this.filters.project_name && this.filters.project_name.length > 0) ||
             this.filters.activities ||
             this.filters.products ||
             (this.filters.sub_activities && this.filters.sub_activities.length > 0);
    },

    // Tasks mostrate nella tabella (server-side search)
    paginatedTasks() {
      return this.tasks;
    },

    // Paginazione server-side
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endIndex() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },

    // API ottimizzata per progetti
    projectListApi() {
      return async (params = {}) => {
        try {
          // Prova prima con list_names (ottimizzato)
          const response = await this.$projectService.list_names({choices: '', ...params});
          return response;
        } catch (error) {
          // Fallback a list normale se list_names non funziona
          const response = await this.$projectService.list({choices: '', ...params});

          // Trasforma i dati per avere solo id e name
          const transformedData = response.data.map(project => ({
            id: project.id,
            name: project.name
          }));

          return {
            ...response,
            data: transformedData
          };
        }
      }
    },

    // Safe services per lazy loading (come nel TasksProject originale)
    safeCustomerService() {
      return this.$customerService && this.$customerService.list ? this.$customerService.list : () => Promise.resolve({data: []});
    },
    safeActivityService() {
      return this.$activityService && this.$activityService.list ? this.$activityService.list : () => Promise.resolve({data: []});
    },
    safeProductService() {
      return this.$productService && this.$productService.list ? this.$productService.list : () => Promise.resolve({data: []});
    },
    safeSubActivityService() {
      return this.$subActivityService && this.$subActivityService.list ? this.$subActivityService.list : () => Promise.resolve({data: []});
    },

    // API per autocomplete codice progetto (dal TasksProject originale)
    projectCodeApi() {
      const API_URL = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000/api/';

      return (params = {}) => {
        try {

          return axios.get(`${API_URL}project/ids/`, {
            headers: authHeader(),
            params: {...params, choices: ''}
          }).then(response => {
            return response;
          }).catch(error => {
            console.error('❌ Errore in projectCodeApi:', error);
            return Promise.resolve({data: []});
          });
        } catch (error) {
          console.error('❌ Errore in projectCodeApi (catch):', error);
          return Promise.resolve({data: []});
        }
      };
    }
  },
  watch: {
    projectId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadData(); // Ricarica i dati quando cambia il progetto
      }
    },
    searchQuery: {
      handler() {
        // Non chiamare searchDebounced qui per evitare perdita focus
        // Il debounce viene gestito direttamente nel template con @input
      }
    },
    quickFilter: {
      handler(value) {
        this.applyQuickFilter(value);
      }
    },
    selectedDate: {
      handler() {
        this.currentPage = 1;
        this.loadData();
      }
    },
    // Reset alla prima pagina quando cambiano i filtri
    sortField() {
      this.currentPage = 1;
      this.loadData();
    },
    sortDirection() {
      this.loadData();
    },
    selectedOperators: {
      handler() {
        this.currentPage = 1;
        this.loadData();
      }
    },
    // Watcher per i nuovi filtri
    filters: {
      handler() {
        this.currentPage = 1;
        this.loadData();
      },
      deep: true
    },
    orderNumberToSearch: {
      handler() {
        this.currentPage = 1;
        this.debouncedLoadData();
      }
    },
    createdAtRangeToSearch() {
      this.currentPage = 1;
      this.loadData();
    },
    projectCode: {
      handler(newCode) {
        if (newCode && typeof newCode === 'object' && newCode.projectId) {
          // Ora riceviamo l'oggetto completo con projectId
          this.projectId = newCode.projectId;
          this.currentPage = 1; // Reset alla prima pagina
          this.loadData(); // Carica i dati filtrati
        } else if (newCode && typeof newCode === 'string') {
          // Fallback per compatibilità - trova il progetto corrispondente al codice
          const apiFunc = this.projectCodeApi;
          if (typeof apiFunc === 'function') {
            apiFunc({choices: ''}).then(
              resp => {
                const project = resp.data.find(p => p.value === newCode);
                if (project && project.projectId) {
                  this.projectId = project.projectId;
                  this.currentPage = 1;
                  this.loadData();
                }
              }
            ).catch(error => {
              console.error('Errore nella ricerca progetto per codice:', error);
            });
          }
        } else if (!newCode) {
          // Se il codice è vuoto, resetta l'ID del progetto
          this.projectId = null;
          this.currentPage = 1;
          this.loadData();
        }
      }
    },
    currentPage() {
      this.loadData();
    }
  },
  created() {
    // Debounce per la ricerca server-side
    this.searchDebounced = debounce(() => {
      this.currentPage = 1; // Reset alla prima pagina per nuova ricerca
      this.loadData(true); // true = è una ricerca
    }, 500);

    // Debounce per il caricamento dati generale
    this.debouncedLoadData = debounce(() => {
      this.loadData();
    }, 300);
  },
  async mounted() {
    // Inizializza il projectId dai parametri di route se presente
    this.projectId = this.$route.params.projectId || this.selectedProjectId;

    await Promise.all([
      this.loadData(),
      this.loadOperators(),
      this.loadDailyOperatorStats()
    ]);
  },
  methods: {
    // Gestione colonne visibili
    isColumnVisible(columnKey) {
      return this.visibleColumns.includes(columnKey);
    },

    // Caricamento dati con paginazione e ricerca lato server
    async loadData(isSearch = false) {
      console.log('🔄 TasksProject2 - loadData started, isSearch:', isSearch);
      this.isSearching = isSearch;

      if (!isSearch) {
        this.loading = true;
        this.startLoadingTimer();
      }

      try {
        const params = {
          page: this.currentPage,
          page_size: this.itemsPerPage,
          ordering: this.sortDirection === 'desc' ? `-${this.sortField}` : this.sortField
        };

        // Ricerca server-side globale
        if (this.searchQuery?.trim()) {
          params.search = this.searchQuery.trim();
        }

        // Gestione filtri data con priorità
        // 1. Priorità al date picker avanzato (created_at_range)
        if (this.createdAtRangeToSearch && this.createdAtRangeToSearch.length >= 2) {
          params.created_at_range = this.createdAtRangeToSearch.join(',');
        }
        // 2. Filtro data singola (date picker)
        else if (this.selectedDate) {
          params.task_date_after = this.selectedDate;
          params.task_date_before = this.selectedDate;
        }
        // 3. Filtro data range (da filtri rapidi: oggi, settimana, mese)
        else if (this.dateRangeStart && this.dateRangeEnd) {
          params.task_date_after = this.dateRangeStart;
          params.task_date_before = this.dateRangeEnd;
        }

        // Filtro progetto
        if (this.projectId) {
          params.project = this.projectId;
          // Carica anche info progetto
          const projectResp = await this.$projectService.get(this.projectId, {detail: ''});
          this.project = projectResp.data.name;
        }

        // Filtro operatori
        if (this.selectedOperators.length > 0) {
          params.operators = this.selectedOperators.join(',');
        }

        // Nuovi filtri dal TasksProject.vue originale
        if (this.filters.customer) {
          params.customer = this.filters.customer;
        }

        if (this.filters.project_name && this.filters.project_name.length > 0) {
          params.project_name = this.filters.project_name.join(',');
        }

        if (this.filters.activities) {
          params.activities = this.filters.activities;
        }

        if (this.filters.products) {
          params.products = this.filters.products;
        }

        if (this.filters.sub_activities && this.filters.sub_activities.length > 0) {
          params.sub_activities = this.filters.sub_activities.join(',');
        }

        if (this.orderNumberToSearch && this.orderNumberToSearch.trim()) {
          params.order_number = this.orderNumberToSearch;
        }

        console.log('📡 TasksProject2 - Fetching tasks with params:', params);
        const response = await this.$taskService.list(params);
        console.log('✅ TasksProject2 - Tasks loaded:', response.data.results?.length, 'total:', response.data.count);

        // Aggiorna dati con risposta server
        this.tasks = response.data.results || [];
        this.totalItems = response.data.count || 0;
        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
        console.log('📊 TasksProject2 - Updated this.tasks, length:', this.tasks.length);

        // Imposta statistiche di base
        this.setBasicStats();

        // Carica le ore totali di tutti i task filtrati per il secondo valore
        this.loadFilteredStats(params);

      } catch (error) {
        console.error('❌ Errore caricamento:', error);
        this.$dtoast.pop({
          preset: "error",
          content: "Errore nel caricamento dei task"
        });
      } finally {
        if (!this.isSearching) {
          this.stopLoadingTimer();
          this.loading = false;
        }
        this.isSearching = false;
      }
    },

    // Carica lista completa operatori per il filtro
    async loadOperators() {
      try {
        const response = await this.$userService.list();

        // API restituisce direttamente l'array in response.data, non in response.data.results
        const users = Array.isArray(response.data) ? response.data : (response.data.results || []);

        this.allOperators = users.map(user => ({
          id: user.id,
          full_name: user.full_name,
          initials: `${user.first_name[0]}${user.last_name[0]}`
        }));

      } catch (error) {
        console.error('❌ Errore caricamento operatori:', error);
      }
    },

    // Timer di caricamento
    startLoadingTimer() {
      this.loadingStartTime = Date.now();
      this.loadingTime = "0.00s";

      this.loadingTimer = setInterval(() => {
        const elapsed = (Date.now() - this.loadingStartTime) / 1000;
        this.loadingTime = elapsed.toFixed(2) + "s";
      }, 10); // Aggiorna ogni 10ms per centesimi di secondo
    },

    stopLoadingTimer() {
      if (this.loadingTimer) {
        clearInterval(this.loadingTimer);
        this.loadingTimer = null;
      }
      if (this.loadingStartTime) {
        const elapsed = (Date.now() - this.loadingStartTime) / 1000;
        this.loadingTime = elapsed.toFixed(2) + "s";
      }
    },

    // Statistiche semplificate per massima velocità
    setBasicStats() {
      // Calcola le ore totali dai task attualmente caricati (filtrati)
      const totalHoursFromCurrentTasks = this.tasks.reduce((sum, task) => {
        return sum + (parseFloat(task.hours) || 0);
      }, 0);

      // Calcola la media per task (sui task caricati)
      const avgHoursPerTask = this.tasks.length > 0
        ? (totalHoursFromCurrentTasks / this.tasks.length).toFixed(1)
        : 0;

      this.taskStats = {
        total_tasks: this.totalItems,
        total_hours: totalHoursFromCurrentTasks.toFixed(1),
        avg_hours_per_task: avgHoursPerTask,
        avg_hours_per_day: 0
      };
      // Non resettiamo dailyOperatorStats qui - viene gestito da loadDailyOperatorStats()
    },

    // Carica statistiche complete per tutti i task filtrati (non solo pagina corrente)
    async loadFilteredStats(params) {
      try {
        // Rimuovi i parametri di paginazione per ottenere stats su tutti i task filtrati
        const statsParams = { ...params };
        delete statsParams.page;
        delete statsParams.page_size;

        const statsResponse = await this.$taskService.stats(statsParams);
        const stats = statsResponse.data;

        // Aggiorna le ore totali di tutti i task filtrati
        if (stats.total_hours !== undefined) {
          // Converti da formato ore:minuti a decimale se necessario
          let totalHours = stats.total_hours;
          if (typeof totalHours === 'string' && totalHours.includes(':')) {
            const [hours, minutes] = totalHours.split(':').map(Number);
            totalHours = (hours + minutes / 60).toFixed(1);
          }

          this.totalFilteredHours = totalHours;
        }

      } catch (error) {
        console.error('❌ Errore caricamento statistiche filtrate:', error);
        // Non bloccare l'interfaccia se le statistiche falliscono
      }
    },

    // Carica statistiche operatori per oggi via API separata
    async loadDailyOperatorStats() {
      try {
        const today = new Date().toISOString().substr(0, 10);

        // Resetta array iniziale per vedere se viene caricato
        this.dailyOperatorStats = [];

        // Prova l'API reale
        const response = await this.$taskService.list({
          task_date_after: today,
          task_date_before: today,
          page_size: 1000
        });
        // Gestisce sia risposta paginata {results: [...]} che array diretto [...]
        const todayTasks = Array.isArray(response.data)
          ? response.data
          : (response.data.results || []);

        if (todayTasks.length > 0) {

          const operatorStats = {};

          todayTasks.forEach((task) => {
            const operatorName = task.operator_full_name ||
                               task.operator_name ||
                               `Operatore ${task.operator}`;

            const operatorId = task.operator || operatorName;

            if (!operatorStats[operatorId]) {
              operatorStats[operatorId] = {
                id: operatorId,
                name: operatorName,
                initials: this.getInitials(operatorName),
                totalMinutes: 0
              };
            }

            operatorStats[operatorId].totalMinutes += task.minutes || 0;
          });

          // Converti in array e calcola ore
          const realStats = Object.values(operatorStats)
            .map(op => ({
              ...op,
              hours: Math.round(op.totalMinutes / 60 * 10) / 10 // Arrotonda a 1 decimale
            }))
            .filter(op => op.hours > 0)
            .sort((a, b) => b.hours - a.hours);

          this.dailyOperatorStats = realStats;

        } else {
          this.dailyOperatorStats = [];
        }

      } catch (error) {
        console.error('❌ ERRORE loadDailyOperatorStats:', error);
        console.error('❌ Error details:', error.response?.data);
        this.dailyOperatorStats = [];
      }
    },

    // Helper per calcolare iniziali
    getInitials(fullName) {
      if (!fullName || fullName === 'Operatore') {
        return 'OP';
      }

      const nameStr = String(fullName).trim();

      // Se è un numero (ID), usa le prime due cifre
      if (/^\d+$/.test(nameStr)) {
        return nameStr.substring(0, 2).toUpperCase();
      }

      // Se contiene spazi, prendi le prime lettere di ogni parola
      if (nameStr.includes(' ')) {
        return nameStr
          .split(' ')
          .filter(word => word.length > 0)
          .map(word => word.charAt(0).toUpperCase())
          .slice(0, 2)
          .join('');
      }

      // Se è una parola singola, prendi le prime due lettere
      return nameStr.substring(0, 2).toUpperCase();
    },

    // Metodi di ordinamento
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },
    getSortIcon(field) {
      if (this.sortField !== field) return 'mdi-sort';
      return this.sortDirection === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
    },

    // La paginazione è gestita automaticamente da v-pagination tramite v-model

    // Filtri rapidi
    applyQuickFilter(filterIndex) {
      const today = new Date();
      let todayStr, startDate, endDate;

      switch(filterIndex) {
        case 0: // Tutti
          this.selectedDate = null;
          this.dateRangeStart = null;
          this.dateRangeEnd = null;
          break;
        case 1: // Oggi
          todayStr = format(today, 'yyyy-MM-dd');
          this.selectedDate = null;
          this.dateRangeStart = todayStr;
          this.dateRangeEnd = todayStr;
          break;
        case 2: // Questa settimana
          startDate = format(startOfWeek(today, {weekStartsOn: 1}), 'yyyy-MM-dd');
          endDate = format(endOfWeek(today, {weekStartsOn: 1}), 'yyyy-MM-dd');
          this.selectedDate = null;
          this.dateRangeStart = startDate;
          this.dateRangeEnd = endDate;
          break;
        case 3: // Questo mese
          startDate = format(startOfMonth(today), 'yyyy-MM-dd');
          endDate = format(endOfMonth(today), 'yyyy-MM-dd');
          this.selectedDate = null;
          this.dateRangeStart = startDate;
          this.dateRangeEnd = endDate;
          break;
      }

      this.updateDateRange();

      // Ricarica i dati con il nuovo filtro applicato
      this.currentPage = 1;
      this.loadData();
    },

    // Gestione date
    updateDateRange() {
      this.dateMenu = false;

      if (this.selectedDate) {
        // Data singola selezionata manualmente
        this.dateRange = this.formatDate(this.selectedDate, 'dd/MM');
        // Reset del range quando selezioni manualmente
        this.dateRangeStart = null;
        this.dateRangeEnd = null;
      } else if (this.dateRangeStart && this.dateRangeEnd) {
        // Range dai filtri rapidi
        if (this.dateRangeStart === this.dateRangeEnd) {
          this.dateRange = this.formatDate(this.dateRangeStart, 'dd/MM');
        } else {
          this.dateRange = `${this.formatDate(this.dateRangeStart, 'dd/MM')} - ${this.formatDate(this.dateRangeEnd, 'dd/MM')}`;
        }
      } else {
        this.dateRange = '';
      }
    },

    // Azioni
    clearFilters() {
      this.searchQuery = '';
      this.clearDateFilters();
      this.quickFilter = 0;
      this.currentPage = 1;
      this.selectedOperators = []; // Reset operatori selezionati

      // Reset nuovi filtri
      this.filters = {
        customer: null,
        project_name: [],
        activities: null,
        products: null,
        sub_activities: []
      };
      this.projectCode = null;
      this.orderNumberToSearch = '';

      // Forza il ricaricamento dei dati
      this.loadData();
    },


    clearDateFilters() {
      this.selectedDate = null;
      this.dateRangeStart = null;
      this.dateRangeEnd = null;
      this.dateRange = '';
      this.createdAtRangeToSearch = [];
    },

    getDateRangeLabel() {
      if (this.createdAtRangeToSearch && this.createdAtRangeToSearch.length >= 2) {
        const start = this.formatDate(this.createdAtRangeToSearch[0], 'dd/MM');
        const end = this.formatDate(this.createdAtRangeToSearch[1], 'dd/MM');
        return `${start} - ${end}`;
      } else if (this.selectedDate) {
        return this.formatDate(this.selectedDate, 'dd/MM');
      } else if (this.dateRange) {
        return this.dateRange;
      }
      return 'Periodo';
    },

    editTask(task) {
      this.editingTaskId = task.id;
      this.showTaskDialog = true;
    },

    deleteTask(task) {
      this.deletingTask = task;
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deletingTask) return;

      try {
        await this.$taskService.delete(this.deletingTask.id);

        // Rimuovi dalla cache locale invece di ricaricare tutto
        const index = this.tasks.findIndex(t => t.id === this.deletingTask.id);
        if (index !== -1) {
          this.tasks.splice(index, 1);
        }

        this.$dtoast.pop({
          preset: "success",
          content: "Task eliminato con successo"
        });
      } catch (error) {
        console.error('Errore eliminazione task:', error);
        this.$dtoast.pop({
          preset: "error",
          content: error.response?.data?.detail || "Errore nell'eliminazione del task"
        });
      } finally {
        this.showDeleteDialog = false;
        this.deletingTask = null;
      }
    },

    onTaskSaved() {
      console.log('📥 TasksProject2 - onTaskSaved called');
      this.showTaskDialog = false;
      this.editingTaskId = null;
      // Ricarica solo se necessario
      console.log('🔄 TasksProject2 - Reloading data...');
      this.loadData();
    },

    async exportTasks() {
      try {
        // Costruisci i parametri per export basati sui filtri attuali
        const params = {
          // Filtri di data - usa la stessa logica di loadData()
          // 1. Priorità al date picker avanzato (created_at_range)
          ...(this.createdAtRangeToSearch && this.createdAtRangeToSearch.length >= 2 && {
            created_at_range: this.createdAtRangeToSearch.join(',')
          }),
          // 2. Filtro data singola (date picker) - solo se non c'è il range avanzato
          ...(!this.createdAtRangeToSearch?.length && this.selectedDate && {
            task_date_after: this.selectedDate,
            task_date_before: this.selectedDate
          }),
          // 3. Filtro data range (da filtri rapidi: oggi, settimana, mese) - solo se non ci sono altri filtri
          ...(!this.createdAtRangeToSearch?.length && !this.selectedDate && this.dateRangeStart && this.dateRangeEnd && {
            task_date_after: this.dateRangeStart,
            task_date_before: this.dateRangeEnd
          }),

          // Filtri selezionati
          ...(this.selectedOperators.length > 0 && {
            operators: this.selectedOperators.join(',')
          }),
          ...(this.projectId && { project: this.projectId }),
          ...(this.filters.activities && {
            activities: this.filters.activities
          }),
          ...(this.filters.products && {
            products: this.filters.products
          }),
          ...(this.filters.sub_activities?.length > 0 && {
            sub_activities: this.filters.sub_activities.join(',')
          }),
          ...(this.filters.customer && {
            customer: this.filters.customer
          }),
          ...(this.projectCode && {
            project_code: this.projectCode
          }),
          ...(this.orderNumberToSearch && {
            order_number: this.orderNumberToSearch
          }),

          // Ricerca globale
          ...(this.searchQuery?.trim() && { search: this.searchQuery.trim() }),

          // Ordinamento
          ordering: this.sortDirection === 'desc' ? `-${this.sortField}` : this.sortField
        };

        console.log('📊 TasksProject2 - Export params:', params);

        this.$dtoast.pop({
          preset: "success",
          content: "Generazione file Excel in corso...",
        });

        const response = await this.$taskService.exportExcel(params);
        const now = format(new Date(), 'dd-MM-yyyy_HH-mm');

        // Nome file più descrittivo
        let filename = `Tasks_${now}.xlsx`;
        if (this.selectedOperators.length === 1) {
          const operator = this.allOperators.find(op => op.id === this.selectedOperators[0]);
          if (operator) {
            filename = `Tasks_${operator.full_name.replace(' ', '_')}_${now}.xlsx`;
          }
        } else if (this.projectId) {
          filename = `Tasks_Progetto_${this.projectId}_${now}.xlsx`;
        }

        this.saveFile(response.data, filename);
        this.$dtoast.pop({
          preset: "success",
          content: "File Excel scaricato con successo!",
        });

      } catch (error) {
        console.error('Errore export:', error);
        this.$dtoast.pop({
          preset: "error",
          content: `Errore nell'esportazione: ${error.message || 'Errore sconosciuto'}`,
        });
      }
    },

    // Utility methods
    formatDate(dateString, formatStr = 'dd/MM/yyyy') {
      if (!dateString) return '-';
      try {
        return format(parseISO(dateString), formatStr);
      } catch {
        return dateString;
      }
    },

    isToday(dateString) {
      if (!dateString) return false;
      try {
        return checkIsToday(parseISO(dateString));
      } catch {
        return false;
      }
    },

    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.slice(0, length) + '...' : text;
    },


    getHoursColor(hours) {
      const h = parseFloat(hours);
      if (h >= 8) return 'success';
      if (h >= 4) return 'primary';
      if (h >= 1) return 'warning';
      return 'grey';
    },

    canDelete(task) {
      const user = this.$store.state.user;
      if (!user) return false;

      // Controlla i permessi di eliminazione task
      if (this.$store.getters.canDeleteAllTasks) {
        return true;
      }

      if (this.$store.getters.canDeleteOwnTasks && user.id === task.operator) {
        return true;
      }

      return false;
    },

    canEditTask(task) {
      const user = this.$store.state.user;
      if (!user) return false;

      // Controlla i permessi di modifica task
      if (this.$store.getters.canEditAllTasks) {
        return true;
      }

      if (this.$store.getters.canEditOwnTasks && user.id === task.operator) {
        return true;
      }

      return false;
    },

    saveFile(data, filename) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
}
</script>

<style scoped>
.tasks-optimized {
  max-width: 100%;
  margin: 0 auto;
}

/* Icona loading che gira */
.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Tabella ottimizzata */
.table-container {
  overflow-x: auto;
  max-height: 70vh;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.tasks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.tasks-table th {
  background: #f5f5f5;
  padding: 12px 8px;
  font-weight: 600;
  border-bottom: 2px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
}

.tasks-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.tasks-table th.sortable:hover {
  background: #eeeeee;
}

.tasks-table td {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.task-row:hover {
  background: #fafafa;
}

.task-row--today {
  background: #e8f5e8;
}

/* Celle specifiche */
.date-cell {
  min-width: 60px;
  width: 60px;
}

.date-main {
  font-weight: 600;
  font-size: 14px;
}

.date-year {
  font-size: 11px;
  color: #666;
}

.operator-cell {
  display: flex;
  align-items: center;
  min-width: 60px;
  justify-content: center;
}

.project-cell {
  min-width: 180px;
}

.project-name {
  font-weight: 500;
}

.project-code {
  font-size: 11px;
}

.activity-cell {
  min-width: 120px;
}

.activity-main {
  font-weight: 500;
}

.activity-sub {
  font-size: 11px;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

/* Responsive */
@media (max-width: 768px) {
  .tasks-table {
    font-size: 12px;
  }

  .tasks-table th,
  .tasks-table td {
    padding: 6px 4px;
  }

  .operator-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

/* Performance ottimizzazioni */
.tasks-table tbody tr {
  will-change: transform;
}

.discrete-loader {
  position: fixed;
  top: 80px;
  right: 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 20px;
  padding: 8px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  z-index: 999;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.discrete-loader .text-caption {
  color: #666;
  font-size: 11px;
  font-weight: 500;
}

.v-progress-circular {
  will-change: transform;
}

/* Evita flash di contenuto */
.table-container {
  contain: layout style paint;
}

/* Campo ricerca più piccolo */
.small-search-field >>> .v-input__slot {
  min-height: 32px !important;
  height: 32px !important;
  padding: 0 8px !important;
}

.small-search-field >>> .v-input__control {
  min-height: 32px !important;
}

.small-search-field >>> input {
  padding: 4px 0 !important;
  font-size: 13px !important;
}

/* Riga filtri nella tabella */
.filter-row {
  background: #fafafa;
  border-bottom: 2px solid #e0e0e0;
}

.filter-row th {
  padding: 4px !important;
  background: transparent !important;
}

.filter-row .v-btn {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.filter-row .v-btn:hover {
  opacity: 1;
}
</style>